import { useCallback, useEffect, useMemo } from 'react';

import { useLocation, useParams } from 'react-router';

import usePermissions from 'api/usePermissions';
import usePublisher from 'api/usePublisher';
import { useUserDetails } from 'api/useUserDetails';
import { EPublisherSolutionModel, UserRole } from 'common/contracts';

import { useSidebar } from './context';
import { ESideBarContext, ESidebarGroupKey, SideBarItem } from './types';
import {
  getCompanyLevelNavItems,
  getPortalLevelNavItems,
  getPortalLevelStoreNavItems,
  getProjectLevelNavItems
} from './utils';

export const useSidebarNavigation = () => {
  const {
    context,
    setContext,
    setNavItems,
    setIsCheckout,
    featureFlags,
    companyFeatureFlags,
    setIsLoading,
    currentProjectSubType,
    setCurrentProjectSubType
  } = useSidebar();
  const { getPermissions, isSuperAdminByProject } = usePermissions();
  const location = useLocation();
  const { publisherId, portalId } = useParams();
  const { getPublisher } = usePublisher(publisherId || undefined);

  const { userDetails } = useUserDetails();
  const projectDetails = userDetails?.projects;

  // Update context based on URL
  useEffect(() => {
    const pathname = location.pathname;
    const isPortalLevelStoreRoute = pathname.includes('/store/');

    const newContext =
      isPortalLevelStoreRoute && publisherId
        ? ESideBarContext.PORTAL_LEVEL_STORE
        : publisherId
          ? ESideBarContext.PROJECT
          : portalId
            ? ESideBarContext.PORTAL
            : ESideBarContext.COMPANY;

    if (newContext !== context) {
      setIsLoading(true);
      setContext(newContext);
    }
  }, [
    location,
    publisherId,
    portalId,
    context,
    projectDetails,
    setContext,
    setIsLoading
  ]);

  const hasSingleProject = projectDetails?.length === 1;

  // Set navigation items based on context
  useEffect(() => {
    const permissions = getPermissions();

    const timer = setTimeout(() => {
      switch (context) {
        case ESideBarContext.COMPANY:
          setNavItems(
            getCompanyLevelNavItems(
              permissions,
              companyFeatureFlags,
              userDetails
            )
          );
          break;
        case ESideBarContext.PROJECT:
          if (!publisherId) {
            break;
          }

          const isSuperAdmin = isSuperAdminByProject(publisherId);

          setNavItems(
            getProjectLevelNavItems({
              permissions,
              projectId: publisherId,
              featureFlags,
              currentProjectSubType,
              hasSingleProject,
              isSuperAdmin,
              userRole: userDetails?.role
            })
          );

          break;
        case ESideBarContext.PORTAL:
          setNavItems(
            getPortalLevelNavItems(permissions, portalId, userDetails)
          );
          break;

        case ESideBarContext.PORTAL_LEVEL_STORE:
          setNavItems(
            getPortalLevelStoreNavItems({
              featureFlags,
              isSuperAdmin: publisherId
                ? isSuperAdminByProject(publisherId)
                : false
            })
          );

          break;
        default:
          setNavItems(
            getCompanyLevelNavItems(
              permissions,
              companyFeatureFlags,
              userDetails
            )
          );
      }

      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [
    context,
    featureFlags,
    userDetails,
    setNavItems,
    setIsLoading,
    getPermissions,
    publisherId,
    portalId,
    companyFeatureFlags,
    currentProjectSubType,
    hasSingleProject
  ]);

  // Check if project is checkout
  useEffect(() => {
    if (getPublisher.data) {
      setIsCheckout(
        getPublisher.data.publisherSolutionModel ===
          EPublisherSolutionModel.CHECKOUT
      );
    }
  }, [publisherId, userDetails, setIsCheckout, getPublisher.data]);

  useEffect(() => {
    if (getPublisher.data?.solutionModelSubtype) {
      setCurrentProjectSubType(getPublisher.data.solutionModelSubtype);
    } else if (currentProjectSubType !== null) {
      setCurrentProjectSubType(null);
    }
  }, [getPublisher.data, currentProjectSubType, setCurrentProjectSubType]);
};

export const useProjectInfo = () => {
  const { userDetails } = useSidebar();
  const { publisherId, portalId } = useParams();

  const getProjectName = () => {
    if (!publisherId) return '';
    const project = userDetails?.projects?.find(
      (p) => p.publisherId === publisherId
    );
    return project?.publisherName || 'Project';
  };

  const getPortalName = () => {
    if (!portalId) return '';
    const portal = userDetails?.portals?.find((p) => p.portalId === portalId);
    return portal?.portalName || 'Portal';
  };

  const getPortalLevelStoreName = (storeId?: string) => {
    if (!storeId) return '';
    const store = userDetails?.portals
      ?.flatMap((portal) => portal.stores || [])
      .find((store) => store.storeId === storeId);
    return store?.storeName || 'Portal Level Store';
  };

  return {
    getProjectName,
    getPortalName,
    getPortalLevelStoreName
  };
};

export const useSidebarUrl = (context: ESideBarContext) => {
  const { publisherId, portalId } = useParams();

  const getNavFullUrl = useCallback(
    (item: SideBarItem, group?: ESidebarGroupKey) => {
      const isStoreContext =
        context === ESideBarContext.PORTAL && group === ESidebarGroupKey.Stores;

      if (isStoreContext) {
        return item.url;
      }

      if (context !== ESideBarContext.COMPANY) {
        return `${context}/${publisherId || portalId}/${item.url}`;
      }

      return item.url;
    },
    [context, publisherId, portalId]
  );

  return { getNavFullUrl };
};
